/**
 * What has actually moved: survey dispatches per channel, and recent events.
 *
 * The chart is counted in the database rather than in the browser. An account
 * that sends a lot is exactly the one whose totals matter, and it is also the
 * one whose rows are too many to download just to add them up.
 */

import React, { useEffect, useRef } from 'react';
import { Panel, Button, Select } from '@cloudsam/ui';
import { formatSurveyCount } from './activityChart';

function when(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
}

function outcomeTag(outcome) {
    if (['selected', 'accepted', 'sent', 'submitted'].includes(outcome)) return 'tag tag--blue';
    if (['pending', 'queued', 'dispatching', 'activation_pending', 'awaiting_recipient', 'completed_with_failures'].includes(outcome)) return 'tag tag--yellow';
    if (['delivered', 'activated', 'started', 'read', 'received'].includes(outcome)) return 'tag tag--green';
    if (['failed', 'error', 'bounced', 'complained'].includes(outcome)) return 'tag tag--red';
    return 'tag tag--neutral';
}

function eventDescription(event) {
    const kind = (event.message_kind || 'message').replaceAll('_', ' ');
    const channel = event.channel || 'channel';
    if (event.direction === 'inbound') return `Inbound ${kind} received on ${channel}`;
    switch (event.outcome) {
        case 'queued': return `${kind} queued for ${channel}`;
        case 'dispatching': return `${kind} dispatch started on ${channel}`;
        case 'failed': return `Outbound ${kind} rejected on ${channel}`;
        case 'cancelled': return `Outbound ${kind} cancelled on ${channel}`;
        case 'bounced': return `Accepted ${kind} later bounced on ${channel}`;
        case 'complained': return `Recipient complaint recorded on ${channel}`;
        case 'delivered': return `${kind} delivered on ${channel}`;
        case 'read': return `${kind} read on ${channel}`;
        default: return `${kind} accepted by ${event.provider || channel}`;
    }
}

function dispatchContext(event) {
    const labels = {
        channel_response: 'Recipient chooses channel',
        direct_channel: 'Direct channel',
        invitation_email: 'Invitation email cost',
        dcm_invitation: 'Direct invitation cost',
        execution_turn: 'Conversation cost',
    };
    const values = [];
    if (event.dispatch_mode) values.push(labels[event.dispatch_mode] || event.dispatch_mode.replaceAll('_', ' '));
    if (event.invitation_transport) values.push(`Invitation: ${event.invitation_transport}`);
    if (event.execution_channel) values.push(`Selected: ${event.execution_channel}`);
    if (event.launch_state) values.push(`Launch: ${event.launch_state.replaceAll('_', ' ')}`);
    if (event.cost_stage) values.push(labels[event.cost_stage] || event.cost_stage.replaceAll('_', ' '));
    if (event.failure_code) values.push(`Failure: ${event.failure_code}`);
    if (event.dispatch_batch_id) values.push(`Batch: ${event.dispatch_batch_id}`);
    if (event.survey_invitation_id) values.push(`Invitation ID: ${event.survey_invitation_id}`);
    if (event.launch_intent_id) values.push(`Launch ID: ${event.launch_intent_id}`);
    return values;
}

export default function ActivityView({
    sends = [], events = [], total = 0, page = 1, rows = 25,
    scaleCeiling = 1, onPage, onRows, search = '', onSearch,
    error, loading = false, unavailableNote,
}) {
    const pages = Math.max(1, Math.ceil(total / rows));
    const listBodyRef = useRef(null);
    const shouldFocusList = useRef(false);

    useEffect(() => {
        if (!shouldFocusList.current) return;
        listBodyRef.current?.focus();
        shouldFocusList.current = false;
    }, [page]);

    function changePage(nextPage) {
        shouldFocusList.current = true;
        onPage?.(nextPage);
    }

    const actions = (
        <>
            <input
                className="input"
                placeholder="Search channel events"
                value={search}
                onChange={onSearch}
                aria-label="Search channel events"
            />
            <Button variant="ghost">Export ▾</Button>
        </>
    );

    return (
        <>
            <Panel title="Sends by channel" count="last 14 days" actions={<Button variant="ghost">Export ▾</Button>}>
                <div className="activity-chart-axis" aria-label={`Scale from 0 to ${formatSurveyCount(scaleCeiling)} surveys`}>
                    <span />
                    <div className="activity-chart-axis-values"><span>0</span><span>{formatSurveyCount(scaleCeiling)}</span></div>
                    <span>Scale</span>
                </div>
                {sends.map((s) => (
                    <div className="bar-row" key={s.channel}>
                        <div>{s.name}</div>
                        <div className="bar-track" aria-label={`${s.name}: ${formatSurveyCount(s.surveys)} surveys`}>
                            {s.surveys > 0 && (
                                <div className="bar-fill" style={{ background: s.color, width: `max(${s.percent}%, 4px)` }} />
                            )}
                        </div>
                        <div className="cell-muted">{formatSurveyCount(s.surveys)} surveys</div>
                    </div>
                ))}
                <div className="panel-footer">
                    Surveys dispatched, counted at the first accepted outbound message. Scale: 0–{formatSurveyCount(scaleCeiling)} surveys. Refreshes every 60 seconds.
                </div>
            </Panel>

            <Panel title="Recent channel events" count={`${total} total`} actions={actions}
                className="managed-history-panel" bodyClassName="managed-history-panel__body">
                <div className="managed-history-scroll" ref={listBodyRef} tabIndex="-1" aria-label="Channel events list">
                    <div className="table-head grid-events managed-history-head">
                        <div>When</div><div>Event</div><div>Binding</div><div>Outcome</div>
                    </div>
                    {error ? (
                        <div className="managed-list-state panel-error">{error}</div>
                    ) : unavailableNote ? (
                        <div className="managed-list-state panel-unavailable">{unavailableNote}</div>
                    ) : loading ? (
                        <div className="managed-list-state">Loading channel events…</div>
                    ) : events.length === 0 ? (
                        <div className="managed-list-state panel-note">
                            {search.trim() ? `No channel events match “${search.trim()}”.` : 'No channel events recorded yet.'}
                        </div>
                    ) : (
                        events.map((e) => {
                            const context = dispatchContext(e);
                            return (
                                <div className="table-row grid-events" key={e.event_id}>
                                    <div className="cell-muted">{when(e.at)}</div>
                                    <div>
                                        <div>{eventDescription(e)}</div>
                                        {context.length > 0 && (
                                            <div className="event-dispatch-context" aria-label="Dispatch context">
                                                {context.join(' · ')}
                                            </div>
                                        )}
                                    </div>
                                    <div className="cell-muted">{e.binding_label || '—'}</div>
                                    <div>
                                        <span className={outcomeTag(e.outcome)}>
                                            {e.outcome ? e.outcome.charAt(0).toUpperCase() + e.outcome.slice(1) : ''}
                                        </span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
                <div className="pager" aria-label="Channel events pagination">
                    <Button variant="secondary" disabled={page <= 1 || loading} onClick={() => changePage(page - 1)}>Previous</Button>
                    <span>Page {page} of {pages} · {total} total</span>
                    <Button variant="secondary" disabled={page >= pages || loading} onClick={() => changePage(page + 1)}>Next</Button>
                    <span>Rows</span>
                    <Select value={rows} onChange={onRows} disabled={loading} aria-label="Channel events rows per page">
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </Select>
                </div>
            </Panel>
        </>
    );
}
